import React from 'react';
import { Box, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import type { ManualLottoEntryFormValues, ManualPayoutEntryFormValues } from '../../../../types/features/storeDetails/manualEntries';

type StoreDetailsManualEntriesTabProps = {
    canShowManualLottoSales: boolean;
    canShowManualPayout: boolean;
    manualLottoPayload: ManualLottoEntryFormValues;
    manualPayoutPayload: ManualPayoutEntryFormValues;
    manualLottoSubmitting: boolean;
    manualPayoutSubmitting: boolean;
    isManualLottoFormValid: boolean;
    isManualPayoutFormValid: boolean;
    todayDate: string;
    onManualLottoPayloadChange: (updater: (previous: ManualLottoEntryFormValues) => ManualLottoEntryFormValues) => void;
    onManualPayoutPayloadChange: (updater: (previous: ManualPayoutEntryFormValues) => ManualPayoutEntryFormValues) => void;
    onSubmitManualLottoSales: () => void;
    onSubmitManualPayout: () => void;
    loadingButton: React.ReactNode;
};

const numberFieldSx = {
    '& input[type=number]': {
        MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
    },
};

const posEntryCardSx = { p: 2, borderRadius: 3, border: '1px solid #C8CEE3', boxShadow: '0px 3px 10px 0px #222B450D' };

const StoreDetailsManualEntriesTab: React.FC<StoreDetailsManualEntriesTabProps> = ({
    canShowManualLottoSales,
    canShowManualPayout,
    manualLottoPayload,
    manualPayoutPayload,
    manualLottoSubmitting,
    manualPayoutSubmitting,
    isManualLottoFormValid,
    isManualPayoutFormValid,
    todayDate,
    onManualLottoPayloadChange,
    onManualPayoutPayloadChange,
    onSubmitManualLottoSales,
    onSubmitManualPayout,
    loadingButton,
}) => (
    <Stack spacing={2}>
        {canShowManualLottoSales && (
            <Paper sx={posEntryCardSx}>
                <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#222B45', mb: 2 }}>
                    Create Vending Machine Lotto Sales
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                    <TextField
                        label="Date"
                        type="date"
                        value={manualLottoPayload.business_date}
                        onChange={(event) => onManualLottoPayloadChange((previous) => ({ ...previous, business_date: event.target.value }))}
                        placeholder="YYYY-MM-DD"
                        slotProps={{
                            inputLabel: { shrink: true },
                            htmlInput: { max: todayDate },
                        }}
                        fullWidth
                    />
                    <TextField
                        label="Lotto Sales Amount"
                        type="number"
                        value={manualLottoPayload.lotto_sales_amount}
                        onChange={(event) => onManualLottoPayloadChange((previous) => ({ ...previous, lotto_sales_amount: event.target.value }))}
                        placeholder="Enter lotto sales amount"
                        slotProps={{
                            input: {
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                            htmlInput: { min: 0, step: '0.01' },
                        }}
                        sx={numberFieldSx}
                        fullWidth
                    />
                    <TextField
                        label="Lotto Sales Qty"
                        type="number"
                        value={manualLottoPayload.lotto_sales_qty}
                        onChange={(event) => onManualLottoPayloadChange((previous) => ({ ...previous, lotto_sales_qty: event.target.value }))}
                        placeholder="Enter lotto sales quantity"
                        slotProps={{
                            htmlInput: { min: 0, step: 1 },
                        }}
                        sx={numberFieldSx}
                        fullWidth
                    />
                </Stack>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    {React.isValidElement(loadingButton)
                        ? React.cloneElement(loadingButton as React.ReactElement<any>, {
                              onClick: onSubmitManualLottoSales,
                              loading: manualLottoSubmitting,
                              disabled: !isManualLottoFormValid || manualLottoSubmitting,
                          })
                        : null}
                </Box>
            </Paper>
        )}

        {canShowManualPayout && (
            <Paper sx={posEntryCardSx}>
                <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#222B45', mb: 2 }}>
                    Create Manual Payout
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                    <TextField
                        label="Date"
                        type="date"
                        value={manualPayoutPayload.business_date}
                        onChange={(event) => onManualPayoutPayloadChange((previous) => ({ ...previous, business_date: event.target.value }))}
                        placeholder="YYYY-MM-DD"
                        slotProps={{
                            inputLabel: { shrink: true },
                            htmlInput: { max: todayDate },
                        }}
                        fullWidth
                    />
                    <TextField
                        label="Payout Amount"
                        type="number"
                        value={manualPayoutPayload.payout_amount}
                        onChange={(event) => onManualPayoutPayloadChange((previous) => ({ ...previous, payout_amount: event.target.value }))}
                        placeholder="Enter payout amount"
                        slotProps={{
                            input: {
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                            htmlInput: { min: 0, step: '0.01' },
                        }}
                        sx={numberFieldSx}
                        fullWidth
                    />
                </Stack>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    {React.isValidElement(loadingButton)
                        ? React.cloneElement(loadingButton as React.ReactElement<any>, {
                              onClick: onSubmitManualPayout,
                              loading: manualPayoutSubmitting,
                              disabled: !isManualPayoutFormValid || manualPayoutSubmitting,
                          })
                        : null}
                </Box>
            </Paper>
        )}
    </Stack>
);

export default React.memo(StoreDetailsManualEntriesTab);
